import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import cn from 'classnames';
import produce from 'immer';

import "./BulkDataTextarea.scss";
import {Button} from 'components';

export interface IBulkDataTextareaProps {
    defaultLines?: string[],
    itemName: string,
    itemNamePlural: string,
    lineValidRegex: RegExp,
    readOnly?: boolean,
}

export interface IBulkDataTextareaAPI {
    getLinesAndValidate: () => {lines: string[], isValid: boolean},
    setTextarea: (lines: string[]) => void,
}

interface ILineEntry {
    lineNum: number,
    value: string,
    key: number,
}

interface IDuplicateEntry {
    lineValue: string,
    lineNumbers: number[],
}

interface IBulkDataModalState {
    duplicateLines: IDuplicateEntry[],
    invalidLines: ILineEntry[],
    isValid: boolean,
    linesCount: number,
    // The textarea is an uncontrolled input for performance. Sometimes, we want to reset its value.
    // To do that, we'll increment a key prop, which will make react rerender it as a new component.
    textareaDefaultVal: string,
    textareaKey: number,
}

export const BulkDataTextarea = forwardRef<IBulkDataTextareaAPI, IBulkDataTextareaProps>(function BulkDataTextarea(props, ref) {
    const {
        defaultLines = [],
        itemName,
        itemNamePlural,
        lineValidRegex,
        readOnly,
    } = props;
    const textareaRef = useRef<HTMLTextAreaElement>();
    const [state, setState] = useState<IBulkDataModalState>(() => {
        const { isValid, dupes, invalidLines } = validate(defaultLines);

        return {
            duplicateLines: dupes,
            invalidLines: invalidLines,
            isValid: isValid,
            linesCount: defaultLines.length,
            textareaDefaultVal: createTextareaString(defaultLines),
            textareaKey: 1,
        };
    })
    const {
        isValid,
        duplicateLines,
        invalidLines,
        linesCount,
        textareaDefaultVal,
        textareaKey,
    } = state;

    // Helper function to avoid duplicate code
    function setTextareaState(lines: string[], isValid: boolean, dupes: IDuplicateEntry[], invalidLines: ILineEntry[]) {
        setState(produce(draft => {
            draft.duplicateLines = dupes;
            draft.isValid = isValid;
            draft.linesCount = lines.length;
            draft.invalidLines = invalidLines;
            // Update the textarea to show our current state. We'll bump its key to make it rerender.
            draft.textareaDefaultVal = createTextareaString(lines);
            draft.textareaKey++;
        }))
    }

    // Expose an API for parent components to call. We're using this method to allow parents to get our value,
    // because it's too performance intensive to call an onChange for every keypress.
    useImperativeHandle(ref, () => ({
        getLinesAndValidate: getLinesAndValidate,
        setTextarea: (lines) => {
            setTextareaState(lines, true, [], [])
        }
    }));

    function getLinesAndValidate(): {lines: string[], isValid: boolean} {
        const lines = getLines();
        const { isValid, dupes, invalidLines } = validate(lines);
        setTextareaState(lines, isValid, dupes, invalidLines);
        
        return {
            lines: lines,
            isValid: isValid
        };
    }

    function createTextareaString(textareaLocations: string[]): string {
        return textareaLocations.join(`,\n`);
    }

    // Parse the textarea's value into an array of lines.
    function getLines(): string[] {
        if (!textareaRef.current) return [];

        const val = textareaRef.current.value;

        // First, split by commas or whitespace.
        // Regex:
        //     \s*     optional whitespace
        //     [,\s]   comma or whitespace
        //     \s*     optional whitespace
        let lines = val.split(/\s*[,\s]\s*/)

        // Trim each line, then remove empty lines
        return lines
            .map(line => line.trim())
            .filter(line => line.length > 0);
    }

    // Validate each line and check for duplicates. This needs to be performant for up to 50k lines,
    // so we're using O(n) algorithms.
    function validate(lines: string[]): {
        isValid: boolean,
        dupes: IDuplicateEntry[],
        invalidLines: ILineEntry[]
    } {
        let invalidLines: ILineEntry[] = [];

        // Calculate duplicates - we'll create a map with the line's value as the key for O(1) lookups.
        const existingLines: {[lineVal: string]: ILineEntry[]} = {};

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const lineEntry: ILineEntry = {
                lineNum: i,
                value: line,
                key: i,
            }

            if (!line.match(lineValidRegex)) {
                invalidLines.push(lineEntry);
            }

            if (!existingLines[line]) {
                // No existing lines so far, not a duplicate yet.
                existingLines[line] = [lineEntry];
            } else {
                // Duplicate detected, add this line to the list.
                existingLines[line].push(lineEntry);
            }
        }

        // Compile an array of all duplicates.
        let dupes: IDuplicateEntry[] = [];
        for (const linesArr of Object.values(existingLines)) {
            if (linesArr.length > 1) {
                dupes.push({
                    lineValue: linesArr[0].value,
                    lineNumbers: linesArr.map(l => l.lineNum),
                });
            }
        }

        return {
            isValid: dupes.length === 0 && invalidLines.length === 0,
            dupes: dupes,
            invalidLines: invalidLines,
        }
    }

    function onRemoveInvalid() {
        let lines = getLines();
        let { isValid, dupes, invalidLines } = validate(lines);

        // Remove invalid lines. First create a map of invalid lines for O(1) lookups
        const invalidLineNums: {[lineNum: number]: boolean} = {};

        for (const line of invalidLines) {
            invalidLineNums[line.lineNum] = true;
        }

        // To be included, a line's index must not be in the invalid line nums map.
        lines = lines.filter((line, i) => {
            return !invalidLineNums[i];
        });

        // Now revalidate, because we might have removed invalid duplicates.
        ({ isValid, dupes, invalidLines } = validate(lines));
        setTextareaState(lines, isValid, dupes, invalidLines);
    }

    function onRemoveDuplicates() {
        let lines = getLines();
        let { isValid, dupes, invalidLines } = validate(lines);

        // Remove duplicates. Create map of existing for fast lookups
        const existing: {[lineVal: string]: boolean} = {};

        lines = lines.filter((line) => {
            if (!existing[line]) {
                // Line has not been added yet - mark it as existing and then include it.
                existing[line] = true;
                return true;
            }
            // Line already exists, so this is a duplicate that should be excluded.
            return false;
        });

        // Now revalidate because we might have removed some invalid duplicates.
        ({ isValid, dupes, invalidLines } = validate(lines));
        setTextareaState(lines, isValid, dupes, invalidLines);
    }

    function pluralizeItem(num: number): string {
        return num === 1 ? itemName : itemNamePlural;
    }

    // There could be up to 50k invalid lines. We can't render that, so we'll limit
    // how many errors we display.
    const errorsLimit = 100;
    const invalidLinesLimited = invalidLines.slice(0, errorsLimit);
    const duplicatesLimited = duplicateLines.slice(0, errorsLimit);
    const didLimitInvalid = invalidLinesLimited.length !== invalidLines.length;
    const didLimitDuplicates = duplicatesLimited.length !== duplicateLines.length;

    return (<div className="bulkDataTextarea">
        <textarea
            className={cn("bulkDataTextarea_textarea", {
                'invalid': !isValid,
            })}
            defaultValue={textareaDefaultVal}
            rows={5}
            key={textareaKey}
            ref={textareaRef}
            readOnly={readOnly}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
        ></textarea>
        <div className="bulkDataTextarea_count">
            {linesCount} total {itemNamePlural}.
        </div>
        {invalidLines.length > 0 && (
            <div className="bulkDataTextarea_errors">
                <div className="bulkDataTextarea_errorHeader">
                    {invalidLines.length} invalid {pluralizeItem(invalidLines.length)}:
                </div>
                <ul className="bulkDataTextarea_errorsList">
                    {invalidLinesLimited.map(l => (
                        <li key={l.key} className="bulkDataTextarea_errorItem">
                            <span className="bulkDataTextarea_errorLine">Line {l.lineNum + 1}:</span>
                            {l.value}
                        </li>
                    ))}
                    {didLimitInvalid && (
                        <li key="limitReached">
                            And {invalidLines.length - invalidLinesLimited.length} more...
                        </li>
                    )}
                </ul>
                <div className="bulkDataTextarea_errorButtons">
                    <Button theme="blueOutline" size="small" onClick={onRemoveInvalid}>
                        Remove Invalid Lines
                    </Button>
                </div>
            </div>
        )}
        {duplicateLines.length > 0 && (
            <div className="bulkDataTextarea_errors">
                <div className="bulkDataTextarea_errorHeader">
                    {duplicateLines.length} {pluralizeItem(duplicateLines.length)} with duplicates:
                </div>
                <ul className="bulkDataTextarea_errorsList">
                    {duplicatesLimited.map(dupe => (
                        <li key={dupe.lineValue}>
                            <span className="bulkDataTextarea_errorLine">{dupe.lineValue}</span>
                            <ul className="bulkDataTextarea_duplicatesList">
                                {dupe.lineNumbers.map(lineNum => (
                                    <li key={lineNum}>
                                        Line {lineNum + 1}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                    {didLimitDuplicates && (
                        <li key="limitReached">
                            And {duplicateLines.length - duplicatesLimited.length} more...
                        </li>
                    )}
                </ul>
                <div className="bulkDataTextarea_errorButtons">
                    <Button theme="blueOutline" size="small" onClick={onRemoveDuplicates}>
                        Remove Duplicates
                    </Button>
                </div>
            </div>
        )}
    </div>);
});